import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { KeyRound, Leaf, Mail, MailCheck, TreeDeciduous } from 'lucide-react';
import authApi from 'ecommerce/api/auth';
import { AUTH_ERROR_TYPE, authErrorType } from 'ecommerce/lib/authError';
import { useApiHandler } from 'ecommerce/lib/useApi';
import { isEmailValid } from 'utils/validator';

import {
  BackLink,
  Divider,
  EmailInput,
  ErrorCallout,
  Field,
  Heading,
  MIN_PASSWORD_LENGTH,
  Note,
  PasswordHint,
  PasswordInput,
  TextLink,
} from './parts';
import styles from './auth.module.scss';

// Sign in options, sign in with email, create account, forgot password and check your email
// (Auth 1-6, docs/auth-flow.md). Same flow as the parabo-web AuthPanel plus the options screen,
// where Ancestry and FamilySearch come first. The mode lives in state, not in the URL: withAuth
// renders this in place of protected pages and sign in returns to router.asPath, so a written
// ?mode= would end up in that redirect.

export const AUTH_MODE = Object.freeze({
  OPTIONS: 'options',
  SIGN_IN: 'signin',
  SIGN_UP: 'signup',
  FORGOT: 'forgot',
  SENT: 'sent',
});

// Same as the resend cooldown the Pencil frame shows (Auth 6).
const RESEND_COOLDOWN_SECONDS = 60;

const EMAIL_ID = 'au-email';
const EMAIL_ERROR_ID = 'au-email-error';
const PASSWORD_ID = 'au-password';
const PASSWORD_ERROR_ID = 'au-password-error';
const PASSWORD_HINT_ID = 'au-password-hint';

const formatCountdown = (seconds) =>
  `${Math.floor(seconds / 60)}:${`${seconds % 60}`.padStart(2, '0')}`;

const useCountdown = () => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  return [seconds, () => setSeconds(RESEND_COOLDOWN_SECONDS)];
};

const AuthPanel = ({
  initialMode,
  countryCode,
  amazon,
  onAncestry,
  onFamilySearch,
  onSignedIn,
  onTrackError,
  onTrack,
}) => {
  const [mode, setMode] = useState(
    Object.values(AUTH_MODE).includes(initialMode) && initialMode !== AUTH_MODE.SENT
      ? initialMode
      : AUTH_MODE.OPTIONS,
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // { field: 'email' | 'password' | 'form', kind } drives which callout shows where.
  const [error, setError] = useState();
  const [cooldown, startCooldown] = useCountdown();
  const titleRef = useRef();
  const modeChanged = useRef(false);
  // Bumped on every mode switch. A response that arrives after the user left the screen
  // must not change the screen they are on now.
  const screenRef = useRef(0);
  // Where the reset link went. The email field can change while the request runs.
  const [sentTo, setSentTo] = useState('');
  // The ref blocks a second submit within the same render, `busy` disables the buttons.
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const api = authApi(countryCode);
  const isSignUp = mode === AUTH_MODE.SIGN_UP;

  useEffect(() => {
    if (!modeChanged.current) return;
    titleRef.current?.querySelector('h1')?.focus();
  }, [mode]);

  const switchMode = (next) => {
    modeChanged.current = true;
    screenRef.current += 1;
    setError();
    setPassword('');
    setMode(next);
  };

  const backToOptions = () => switchMode(AUTH_MODE.OPTIONS);

  const chooseOption = (option, action) => {
    // Sent before the partner redirect starts, while the page can still deliver it.
    onTrack('signin_option_click', { option });
    action();
  };

  const failValidation = (field, kind, errorType) => {
    setError({ field, kind });
    onTrackError(errorType, isSignUp);
  };

  const validateEmail = () => {
    if (!email) {
      failValidation('email', 'missing', AUTH_ERROR_TYPE.MISSING_FIELDS);
      return false;
    }
    if (!isEmailValid(email)) {
      failValidation('email', 'invalid', AUTH_ERROR_TYPE.INVALID_EMAIL);
      return false;
    }
    return true;
  };

  const signIn = useApiHandler(async () => {
    const screen = screenRef.current;
    const { data, error: apiError } = await api.signIn(email, password);
    // A successful sign in still counts after the user left the screen.
    if (data) {
      await onSignedIn(data, 'signin');
      return;
    }
    if (screen !== screenRef.current) return;

    if (apiError.code !== 401) {
      setError({ field: 'form', kind: 'signin' });
      onTrackError(authErrorType(apiError), false);
      return;
    }

    // Sign in answers one 401 for every failure. Ask whether the account exists, so the
    // message can point at the email or at the password.
    const { data: check } = await api.checkEmail(email);
    if (screen !== screenRef.current) return;
    if (check?.exists === false) {
      setError({ field: 'email', kind: 'unknown' });
      onTrackError(AUTH_ERROR_TYPE.UNKNOWN_EMAIL, false);
      return;
    }

    setError({ field: 'password', kind: check ? 'wrong' : 'mismatch' });
    onTrackError(AUTH_ERROR_TYPE.INVALID_CREDENTIALS, false);
  });

  const signUp = useApiHandler(async () => {
    const screen = screenRef.current;
    const { data, error: apiError } = await api.signUp(email, password);
    if (data) {
      await onSignedIn(data, 'signup');
      return;
    }
    if (screen !== screenRef.current) return;

    const errorType = authErrorType(apiError);
    setError(
      errorType === AUTH_ERROR_TYPE.EMAIL_TAKEN
        ? { field: 'email', kind: 'taken' }
        : { field: 'form', kind: 'signup' },
    );
    onTrackError(errorType, true);
  });

  const sendResetLink = useApiHandler(async () => {
    const screen = screenRef.current;
    const { error: apiError } = await api.resetPassword(email);
    if (screen !== screenRef.current) return;
    if (apiError) {
      setError(
        apiError === 404
          ? { field: 'email', kind: 'noPassword' }
          : { field: 'form', kind: 'reset' },
      );
      return;
    }

    onTrack('password_reset_requested');
    setSentTo(email);
    startCooldown();
    switchMode(AUTH_MODE.SENT);
  });

  const resend = useApiHandler(async () => {
    const screen = screenRef.current;
    const { error: apiError } = await api.resetPassword(sentTo);
    if (screen !== screenRef.current) return;
    if (apiError) {
      setError({ field: 'form', kind: 'reset' });
      return;
    }

    setError();
    onTrack('password_reset_requested', { resend: true });
    startCooldown();
  });

  const runExclusive = async (handler) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await handler();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError();

    if (!validateEmail()) return;

    if (mode === AUTH_MODE.FORGOT) {
      runExclusive(sendResetLink);
      return;
    }

    if (!password) {
      failValidation('password', 'missing', AUTH_ERROR_TYPE.MISSING_FIELDS);
      return;
    }

    if (isSignUp && password.length < MIN_PASSWORD_LENGTH) {
      failValidation('password', 'short', AUTH_ERROR_TYPE.INVALID_PASSWORD);
      return;
    }

    if (isSignUp) {
      runExclusive(signUp);
    } else {
      runExclusive(signIn);
    }
  };

  const emailError = error?.field === 'email' ? error.kind : undefined;
  const passwordError = error?.field === 'password' ? error.kind : undefined;
  const formError = error?.field === 'form' ? error.kind : undefined;

  const emailCallout = {
    missing: { title: 'Enter your email address.' },
    invalid: { title: 'This email address does not look right.', body: 'Check it for typos.' },
    unknown: {
      title: 'There is no MyCanvas account with this email.',
      body: 'If you signed in with Ancestry or FamilySearch before, go back and use that button.',
      actions: (
        <>
          <TextLink onClick={() => switchMode(AUTH_MODE.SIGN_UP)}>Create an account</TextLink>
          <TextLink onClick={backToOptions}>All sign in options</TextLink>
        </>
      ),
    },
    taken: {
      title: 'This email already has a MyCanvas account.',
      actions: (
        <>
          <TextLink onClick={() => switchMode(AUTH_MODE.SIGN_IN)}>Sign in instead</TextLink>
          <TextLink onClick={() => switchMode(AUTH_MODE.FORGOT)}>Reset password</TextLink>
        </>
      ),
    },
    noPassword: {
      title: 'There is no MyCanvas password for this email.',
      body: 'If you signed in with Ancestry, FamilySearch or Amazon, use that button on the sign in page.',
      actions: <TextLink onClick={backToOptions}>Back to sign in</TextLink>,
    },
  }[emailError];

  const resetAction = (
    <TextLink onClick={() => switchMode(AUTH_MODE.FORGOT)}>Reset your password</TextLink>
  );

  const passwordCallout = {
    missing: { title: 'Enter your password.' },
    short: { title: `Use at least ${MIN_PASSWORD_LENGTH} characters.` },
    wrong: { title: 'This password does not match the email.', actions: resetAction },
    // The account check failed, so do not claim the email exists.
    mismatch: { title: 'The email or password is incorrect.', actions: resetAction },
  }[passwordError];

  const formCallout = {
    signin: 'We could not sign you in. Please try again.',
    signup: 'We could not create your account. Please try again.',
    reset: 'We could not send the link. Please try again.',
  }[formError];

  const emailField = (
    <Field id={EMAIL_ID} label="Email">
      <EmailInput
        id={EMAIL_ID}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        invalid={Boolean(emailCallout)}
        errorId={EMAIL_ERROR_ID}
      />
      {emailCallout && (
        <ErrorCallout id={EMAIL_ERROR_ID} title={emailCallout.title} actions={emailCallout.actions}>
          {emailCallout.body}
        </ErrorCallout>
      )}
    </Field>
  );

  const formAlert = formCallout && <ErrorCallout title={formCallout} />;

  if (mode === AUTH_MODE.OPTIONS) {
    return (
      <div className={styles.stack} ref={titleRef}>
        <Heading title="Sign in to MyCanvas">
          Use your Ancestry or FamilySearch login. Your family trees come with you.
        </Heading>

        <div className={styles.partners}>
          <button
            type="button"
            className={styles.ancestry}
            onClick={() => chooseOption('ancestry', onAncestry)}
          >
            <Leaf size={22} aria-hidden="true" />
            Login with Ancestry
          </button>
          <button
            type="button"
            className={styles.familySearch}
            onClick={() => chooseOption('familysearch', onFamilySearch)}
          >
            <TreeDeciduous size={22} aria-hidden="true" />
            Login with FamilySearch
          </button>
          <p className={styles.loginNote}>
            <KeyRound size={18} aria-hidden="true" />
            No new password. Your Ancestry or FamilySearch login works here.
          </p>
        </div>

        <Divider />

        <div className={styles.others}>
          <p className={styles.othersTitle}>No Ancestry or FamilySearch account?</p>
          <div className={styles.otherButtons}>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => chooseOption('email', () => switchMode(AUTH_MODE.SIGN_IN))}
            >
              <Mail size={22} aria-hidden="true" />
              Continue with email
            </button>
            {amazon}
          </div>
        </div>
      </div>
    );
  }

  if (mode === AUTH_MODE.SENT) {
    return (
      <div className={styles.stack} ref={titleRef}>
        <Heading icon={MailCheck} title="Check your email">
          We sent a reset link to <strong className={styles.email}>{sentTo}</strong>. The link works
          for 48 hours.
        </Heading>
        <Note>Not in your inbox after a few minutes? Check the spam or promotions folder.</Note>
        {formAlert}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            disabled={cooldown > 0 || busy}
            onClick={() => runExclusive(resend)}
          >
            {cooldown > 0 ? `Send again in ${formatCountdown(cooldown)}` : 'Send again'}
          </button>
          <div className={styles.links}>
            <TextLink onClick={() => switchMode(AUTH_MODE.FORGOT)}>Use a different email</TextLink>
            <TextLink onClick={backToOptions}>Back to sign in</TextLink>
          </div>
        </div>
      </div>
    );
  }

  if (mode === AUTH_MODE.FORGOT) {
    return (
      <form className={styles.stack} ref={titleRef} onSubmit={handleSubmit} noValidate>
        <BackLink onClick={backToOptions}>Back to sign in</BackLink>
        <Heading title="Reset your password">
          Enter the email you use for MyCanvas. We will send you a link to set a new password.
        </Heading>
        <div className={styles.fields}>
          {emailField}
          {formAlert}
          <button type="submit" className={styles.primary} disabled={busy}>
            Send reset link
          </button>
        </div>
        <Note title="Do you sign in with Ancestry or FamilySearch?">
          Then you have no MyCanvas password to reset. Go back and use that button.
        </Note>
      </form>
    );
  }

  return (
    <form className={styles.stack} ref={titleRef} onSubmit={handleSubmit} noValidate>
      <BackLink onClick={backToOptions}>All sign in options</BackLink>
      {isSignUp ? (
        <Heading title="Create an account">Sign up with your email and a password.</Heading>
      ) : (
        <Heading title="Sign in with email">
          Enter the email and password of your MyCanvas account.
        </Heading>
      )}

      <div className={styles.fields}>
        {emailField}
        <Field id={PASSWORD_ID} label="Password">
          <PasswordInput
            id={PASSWORD_ID}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            invalid={Boolean(passwordCallout)}
            errorId={PASSWORD_ERROR_ID}
            hintId={isSignUp ? PASSWORD_HINT_ID : undefined}
          />
          {passwordCallout && (
            <ErrorCallout
              id={PASSWORD_ERROR_ID}
              title={passwordCallout.title}
              actions={passwordCallout.actions}
            />
          )}
          {isSignUp && <PasswordHint id={PASSWORD_HINT_ID} />}
        </Field>
        {!isSignUp && (
          <div>
            <TextLink onClick={() => switchMode(AUTH_MODE.FORGOT)}>Forgot your password?</TextLink>
          </div>
        )}
        {formAlert}
        <button type="submit" className={styles.primary} disabled={busy}>
          {isSignUp ? 'Create account' : 'Sign in'}
        </button>
        <p className={styles.switch}>
          {isSignUp ? 'Already have an account?' : 'New to MyCanvas?'}{' '}
          <TextLink onClick={() => switchMode(isSignUp ? AUTH_MODE.SIGN_IN : AUTH_MODE.SIGN_UP)}>
            {isSignUp ? 'Sign in' : 'Create an account'}
          </TextLink>
        </p>
      </div>
    </form>
  );
};

AuthPanel.propTypes = {
  initialMode: PropTypes.string,
  countryCode: PropTypes.string,
  // The Amazon button, rendered by the host so it keeps the redeem flow.
  amazon: PropTypes.node,
  // Start the partner OAuth redirect. The host owns the return URL.
  onAncestry: PropTypes.func.isRequired,
  onFamilySearch: PropTypes.func.isRequired,
  onSignedIn: PropTypes.func.isRequired,
  onTrackError: PropTypes.func.isRequired,
  onTrack: PropTypes.func.isRequired,
};

export default AuthPanel;
